import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import usePatientInsurance from 'hooks/usePatientInsurance';
import { fetchAdmissions } from 'utils/apiHelper';
import { getAdmissionsFromResponse, getDefaultBillingProvider } from 'utils/invoiceBillingProvider';

const BillingProviderSelector = ({ patientId, value, onChange }) => {
  const { allPolicies, insuranceLoading } = usePatientInsurance(patientId);
  const [admissionLoading, setAdmissionLoading] = useState(false);
  const [hasActiveAdmission, setHasActiveAdmission] = useState(false);
  const [admissionCheckedPatientId, setAdmissionCheckedPatientId] = useState(null);
  const initializedPatientRef = useRef(null);

  useEffect(() => {
    initializedPatientRef.current = null;
    setHasActiveAdmission(false);
    setAdmissionCheckedPatientId(null);
    if (!patientId) return undefined;

    let cancelled = false;
    setAdmissionLoading(true);
    fetchAdmissions({ patient: patientId, status: 'ACTIVE', limit: 1 })
      .then((response) => {
        const admissions = getAdmissionsFromResponse(response);
        if (!cancelled) setHasActiveAdmission(admissions.length > 0);
      })
      .catch(() => {
        if (!cancelled) setHasActiveAdmission(false);
      })
      .finally(() => {
        if (!cancelled) {
          setAdmissionCheckedPatientId(String(patientId));
          setAdmissionLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [patientId]);

  useEffect(() => {
    if (!patientId || insuranceLoading || admissionLoading) return;
    if (admissionCheckedPatientId !== String(patientId)) return;
    if (initializedPatientRef.current === patientId) return;

    const defaultProvider = getDefaultBillingProvider({
      value,
      policies: allPolicies,
      hasActiveAdmission,
    });
    initializedPatientRef.current = patientId;
    onChange(defaultProvider);
  }, [patientId, insuranceLoading, admissionLoading, admissionCheckedPatientId, hasActiveAdmission, allPolicies, value, onChange]);

  if (!patientId) return null;

  const loading = insuranceLoading || admissionLoading;

  return (
    <Box sx={{ pt: 1.5, mt: 0.5, borderTop: 1, borderColor: 'divider' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <HealthAndSafetyOutlinedIcon color="primary" sx={{ fontSize: 19 }} />
        <Typography variant="subtitle2" fontWeight={700}>Billing Provider</Typography>
        {loading && <CircularProgress size={15} />}
      </Stack>
      <FormControl fullWidth disabled={loading}>
        <RadioGroup value={value || ''} onChange={(event) => onChange(event.target.value)}>
          <FormControlLabel
            value="self"
            control={<Radio size="small" />}
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <PersonOutlineIcon sx={{ fontSize: 18 }} />
                <Typography variant="body2" fontWeight={600}>Self / Default Rates</Typography>
              </Stack>
            }
            sx={{ m: 0, py: 0.5 }}
          />
          {allPolicies.map((policy) => {
            const insurer = policy.insurer_details || policy.insurer || {};
            return (
              <FormControlLabel
                key={policy.id}
                value={String(policy.id)}
                control={<Radio size="small" />}
                label={
                  <Stack spacing={0.25} sx={{ py: 0.5 }}>
                    <Typography variant="body2" fontWeight={700}>
                      {insurer.name || policy.insurer_name || 'Insurance Provider'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Policy: {policy.policy_number || 'Not provided'} | Member ID: {policy.member_id || 'Not provided'} | Valid till: {policy.valid_till || 'No expiry'}
                    </Typography>
                  </Stack>
                }
                sx={{ m: 0, alignItems: 'flex-start' }}
              />
            );
          })}
        </RadioGroup>
      </FormControl>
    </Box>
  );
};

BillingProviderSelector.propTypes = {
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

export default BillingProviderSelector;
